import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  Button,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { createMerchant } from "../api/merchants";

// País + bandera + nombre legible. Mantener corta — los providers actuales
// soportan PY/UY/AR/BR/CL/CO/MX/PE. Si agregamos más, sumar acá.
const COUNTRIES = [
  { code: "PY", flag: "🇵🇾", name: "Paraguay" },
  { code: "UY", flag: "🇺🇾", name: "Uruguay" },
  { code: "AR", flag: "🇦🇷", name: "Argentina" },
  { code: "BR", flag: "🇧🇷", name: "Brasil" },
  { code: "CL", flag: "🇨🇱", name: "Chile" },
  { code: "CO", flag: "🇨🇴", name: "Colombia" },
  { code: "MX", flag: "🇲🇽", name: "México" },
  { code: "PE", flag: "🇵🇪", name: "Perú" },
];

export function MerchantNewPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", defaultCountry: "PY" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await createMerchant({
        name: form.name.trim(),
        email: form.email.trim(),
        defaultCountry: form.defaultCountry,
      });
      navigate(`/merchants/${created.id}?created=1`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos crear el comercio");
    } finally {
      setSaving(false);
    }
  }

  const country = COUNTRIES.find((c) => c.code === form.defaultCountry);

  return (
    <Stack spacing={2}>
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/merchants")} size="small">
          Volver
        </Button>
      </Box>

      <Typography variant="h5" fontWeight={700}>
        Nuevo comercio
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Completá los datos básicos. Las API keys y la pasarela se configuran después.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }} component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Nombre del comercio"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              autoFocus
              helperText="Cómo se va a llamar en tableros y reportes."
              placeholder="Ej.: Distribuidora Aurora S.A."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Email de contacto"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              required
              helperText="Acá llegan notificaciones administrativas."
              placeholder="contacto@empresa.com"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="País principal"
              value={form.defaultCountry}
              onChange={(e) => set("defaultCountry", e.target.value)}
              required
              helperText="Define la moneda y métodos disponibles por defecto."
              SelectProps={{
                renderValue: () =>
                  country ? `${country.flag}  ${country.name}` : form.defaultCountry,
              }}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  <Box component="span" sx={{ mr: 1.5, fontSize: 20 }}>
                    {c.flag}
                  </Box>
                  <ListItemText primary={c.name} secondary={c.code} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1.5} mt={3}>
          <Button onClick={() => navigate("/merchants")} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Creando…" : "Crear comercio"}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
